/**
 * Kassaflödesanalys enligt indirekt metod (K3/ÅRL)
 * Beräknar kassaflöde baserat på resultaträkning och balansräkningsförändringar
 */

import type { SIEProcessedData, BalanceSheetItem } from '@/components/sie/types';

export interface CashFlowOperatingActivities {
  operatingResult: number;
  depreciation: number;
  provisionsChange: number;
  otherNonCashItems: number;
  subtotalBeforeWorkingCapital: number;
  interestReceived: number;
  interestPaid: number;
  taxPaid: number;
  cashFlowBeforeWorkingCapital: number;
  inventoryChange: number;
  receivablesChange: number;
  otherCurrentAssetsChange: number;
  payablesChange: number;
  otherCurrentLiabilitiesChange: number;
  workingCapitalChange: number;
  total: number;
}

export interface CashFlowInvestingActivities {
  tangibleAssetsPurchase: number;
  tangibleAssetsSale: number;
  intangibleAssetsChange: number;
  financialAssetsChange: number;
  total: number;
}

export interface CashFlowFinancingActivities {
  newEquity: number;
  dividendPaid: number;
  loansReceived: number;
  loansRepaid: number;
  total: number;
}

export interface CashFlowDebug {
  rawCurrentEquity: number;
  rawPreviousEquity: number;
  netResultUsed: number;
  currEquityExResult: number;
  equityChange: number;
  operatingResultRaw: number;
  financialIncomeRaw: number;
  financialExpensesRaw: number;
  resultAfterFinancialRaw: number;
}

export interface CashFlowData {
  operatingActivities: CashFlowOperatingActivities;
  investingActivities: CashFlowInvestingActivities;
  financingActivities: CashFlowFinancingActivities;
  yearCashFlow: number;
  openingCash: number;
  closingCash: number;
  cashFlowCheck: number; // Difference between calculated and actual change
  isBalanced: boolean;
  debug: CashFlowDebug;
}

/**
 * Hjälpfunktion för att summera belopp för konton inom ett visst intervall
 */
function sumAccountsInRange(
  items: BalanceSheetItem[] | undefined,
  startAccount: string,
  endAccount: string
): number {
  if (!items) return 0;
  return items
    .filter(item => {
      const acc = item.account;
      return acc >= startAccount && acc <= endAccount;
    })
    .reduce((sum, item) => sum + item.amount, 0);
}

/**
 * Hämta totalt belopp för en specifik kontokategori
 */
function getAccountTotal(
  items: BalanceSheetItem[] | undefined,
  accountPrefix: string
): number {
  if (!items) return 0;
  return items
    .filter(item => item.account.startsWith(accountPrefix))
    .reduce((sum, item) => sum + item.amount, 0);
}

/**
 * Hämta belopp för ett specifikt konto
 */
function getAccountAmount(
  items: BalanceSheetItem[] | undefined,
  account: string
): number {
  if (!items) return 0;
  const item = items.find(i => i.account === account);
  return item?.amount || 0;
}

/**
 * Hämta summa för konton som matchar ett mönster (t.ex. "209" för alla 209x-konton)
 */
function getAccountsMatchingPattern(
  items: BalanceSheetItem[] | undefined,
  pattern: string
): number {
  if (!items) return 0;
  return items
    .filter(item => item.account.startsWith(pattern))
    .reduce((sum, item) => sum + item.amount, 0);
}

/**
 * Beräknar kassaflödesanalys enligt indirekt metod
 * 
 * @param currentYear - SIE-data för innevarande år
 * @param previousYear - SIE-data för föregående år
 * @returns CashFlowData med alla kassaflödesposter
 */
export function calculateCashFlow(
  currentYear: SIEProcessedData,
  previousYear: SIEProcessedData
): CashFlowData {
  const currBS = currentYear.aggregated.balanceSheet;
  const prevBS = previousYear.aggregated.balanceSheet;
  const incomeStatement = currentYear.aggregated.incomeStatement;

  // ========================================
  // DEN LÖPANDE VERKSAMHETEN
  // ========================================
  
  // --------------------------------------------------------
  // TECKENKONVENTION I SIE/BAS:
  // - Tillgångar (1xxx): positiva (debet)
  // - Skulder (2xxx): negativa (kredit)
  // - Eget kapital (20xx): negativt (kredit)
  // - Intäkter (3xxx): negativa (kredit)
  // - Kostnader (4xxx-7xxx): positiva (debet)
  // - Finansiella intäkter (80xx-82xx): negativa (kredit)
  // - Finansiella kostnader (83xx-84xx): positiva (debet)
  // - Resultat: vinst = negativt, förlust = positivt
  // --------------------------------------------------------

  // Rörelseresultat - detta är redan normaliserat i incomeStatement
  // (vinst visas som positivt tal)
  const operatingResult = incomeStatement.operatingResult;

  // Avskrivningar (konto 78xx i resultaträkningen)
  // Dessa är positiva (debet/kostnad), vi vill ha absolutvärdet
  const depreciation = Math.abs(
    incomeStatement.expenses
      .filter(e => e.account.startsWith('78'))
      .reduce((sum, e) => sum + e.amount, 0)
  );

  // Förändring avsättningar (22xx) - skulder är negativa i SIE
  // Ökning av avsättning = mer negativ = positiv kassaeffekt
  const currProvisions = currBS.totalProvisions || 0;
  const prevProvisions = prevBS.totalProvisions || 0;
  // OBS: Skulder är negativa, så vi normaliserar
  const provisionsChange = Math.abs(currProvisions) - Math.abs(prevProvisions);

  // Övriga ej kassapåverkande poster
  const otherNonCashItems = 0;

  const subtotalBeforeWorkingCapital = operatingResult + depreciation + provisionsChange + otherNonCashItems;

  // Finansiella poster
  // I resultaträkningen är dessa redan normaliserade:
  // totalFinancialIncome = positivt tal (intäkter)
  // totalFinancialExpenses = negativt tal (kostnader som redan är tagna som absolut)
  const interestReceived = Math.abs(incomeStatement.totalFinancialIncome || 0);
  const interestPaid = Math.abs(incomeStatement.totalFinancialExpenses || 0);
  const taxPaid = Math.abs(incomeStatement.totalTaxes || 0);

  const cashFlowBeforeWorkingCapital = subtotalBeforeWorkingCapital + interestReceived - interestPaid - taxPaid;

  // Förändring av rörelsekapital
  // Varulager (14xx)
  const currInventory = sumAccountsInRange(currBS.currentAssets, '1400', '1499');
  const prevInventory = sumAccountsInRange(prevBS.currentAssets, '1400', '1499');
  const inventoryChange = -(currInventory - prevInventory); // Ökning = negativt kassaflöde

  // Kundfordringar (15xx-16xx)
  const currReceivables = sumAccountsInRange(currBS.currentAssets, '1500', '1699');
  const prevReceivables = sumAccountsInRange(prevBS.currentAssets, '1500', '1699');
  const receivablesChange = -(currReceivables - prevReceivables);

  // Övriga kortfristiga fordringar (17xx-18xx)
  const currOtherCurrentAssets = sumAccountsInRange(currBS.currentAssets, '1700', '1899');
  const prevOtherCurrentAssets = sumAccountsInRange(prevBS.currentAssets, '1700', '1899');
  const otherCurrentAssetsChange = -(currOtherCurrentAssets - prevOtherCurrentAssets);

  // Leverantörsskulder (24xx) - skulder är negativa i SIE
  const currPayables = Math.abs(sumAccountsInRange(currBS.shortTermLiabilities, '2400', '2499'));
  const prevPayables = Math.abs(sumAccountsInRange(prevBS.shortTermLiabilities, '2400', '2499'));
  const payablesChange = currPayables - prevPayables; // Ökning = positivt kassaflöde

  // Övriga kortfristiga skulder (25xx-29xx) - skulder är negativa i SIE
  const currOtherLiabilities = Math.abs(sumAccountsInRange(currBS.shortTermLiabilities, '2500', '2999'));
  const prevOtherLiabilities = Math.abs(sumAccountsInRange(prevBS.shortTermLiabilities, '2500', '2999'));
  const otherCurrentLiabilitiesChange = currOtherLiabilities - prevOtherLiabilities;

  const workingCapitalChange = 
    inventoryChange + 
    receivablesChange + 
    otherCurrentAssetsChange + 
    payablesChange + 
    otherCurrentLiabilitiesChange;

  const operatingTotal = cashFlowBeforeWorkingCapital + workingCapitalChange;

  // ========================================
  // INVESTERINGSVERKSAMHETEN
  // ========================================

  // Materiella anläggningstillgångar (11xx-12xx)
  // Nettometod: vi ser bara förändring i balansräkning
  const currTangible = sumAccountsInRange(currBS.fixedAssets, '1100', '1299');
  const prevTangible = sumAccountsInRange(prevBS.fixedAssets, '1100', '1299');
  const tangibleChange = currTangible - prevTangible;
  
  // Vi behöver justera för avskrivningar för att få fram investeringar
  // Investering ≈ -(förändring i bokfört värde + avskrivningar)
  const tangibleAssetsPurchase = tangibleChange < 0 ? 0 : -(tangibleChange + depreciation);
  const tangibleAssetsSale = tangibleChange < 0 ? Math.abs(tangibleChange) : 0;

  // Immateriella anläggningstillgångar (10xx)
  const currIntangible = sumAccountsInRange(currBS.fixedAssets, '1000', '1099');
  const prevIntangible = sumAccountsInRange(prevBS.fixedAssets, '1000', '1099');
  const intangibleAssetsChange = -(currIntangible - prevIntangible);

  // Finansiella anläggningstillgångar (13xx)
  const currFinancialAssets = currBS.totalFinancialAssets || 0;
  const prevFinancialAssets = prevBS.totalFinancialAssets || 0;
  const financialAssetsChange = -(currFinancialAssets - prevFinancialAssets);

  const investingTotal = tangibleAssetsPurchase + tangibleAssetsSale + intangibleAssetsChange + financialAssetsChange;

  // ========================================
  // FINANSIERINGSVERKSAMHETEN
  // ========================================
  
  // --------------------------------------------------------
  // EGET KAPITAL - TECKENHANTERING:
  // I SIE är eget kapital negativt (kredit).
  // Årets resultat ingår INTE i totalEquity från balansräkningen
  // eftersom det redan är separerat till netResult i resultaträkningen.
  // 
  // För att beräkna utdelning behöver vi analysera specifika konton:
  // - Konto 2091: Balanserad vinst
  // - Konto 2098: Vinst/förlust föregående år
  // - Konto 2099: Årets resultat (om det ligger där)
  // 
  // Utdelning syns typiskt som:
  // 1. Minskning av konto 2098 (förra årets vinst betalas ut)
  // 2. Minskning av konto 2091 (balanserad vinst betalas ut)
  // --------------------------------------------------------
  
  // Normalisera eget kapital (ta absolutvärden)
  const rawCurrEquity = currBS.totalEquity || 0;
  const rawPrevEquity = prevBS.totalEquity || 0;
  const currEquityNorm = Math.abs(rawCurrEquity);
  const prevEquityNorm = Math.abs(rawPrevEquity);
  
  // Årets resultat (från resultaträkningen, positivt = vinst)
  const netResult = incomeStatement.netResult;
  
  // Eget kapital exklusive årets resultat
  // Om totalEquity redan inkluderar årets resultat som negativ kredit,
  // behöver vi lägga till netResult (eftersom vinst är negativt i EK)
  // Men om resultaträkningen är separat, är totalEquity redan exkl. årets resultat
  const currEquityExResult = currEquityNorm - netResult;
  
  // Beräkna förändring av eget kapital exkl. årets resultat
  const equityChange = currEquityExResult - prevEquityNorm;
  
  // Leta efter specifika utdelningsindikatorer i konto 2098/2091
  // Konto 2098 "Vinst eller förlust från föregående år"
  const curr2098 = Math.abs(getAccountAmount(currBS.equity, '2098'));
  const prev2098 = Math.abs(getAccountAmount(prevBS.equity, '2098'));
  const change2098 = curr2098 - prev2098;
  
  // Konto 2091 "Balanserad vinst eller förlust"
  const curr2091 = Math.abs(getAccountAmount(currBS.equity, '2091'));
  const prev2091 = Math.abs(getAccountAmount(prevBS.equity, '2091'));
  const change2091 = curr2091 - prev2091;
  
  // Utdelning indikeras av minskning i dessa konton
  // (men bara om det inte motsvaras av ökning i 2091 från överföring av förra årets resultat)
  let dividendPaid = 0;
  
  // Om 2098 har minskat mer än vad som förklaras av normal överföring
  if (change2098 < 0) {
    // Föregående års resultat borde ha flyttats till 2091 vid årsskifte
    // Om 2098 ändå minskar kan det vara utdelning
    dividendPaid = Math.abs(change2098);
  }
  
  // Alternativ metod: om total EK-förändring är negativ och större än vad som
  // kan förklaras av förlust, kan det vara utdelning
  // Men vi håller oss till konto-specifik analys för nu
  
  // Nyemission/tillskott - positiv förändring av eget kapital (exkl. resultat)
  const newEquity = equityChange > 0 ? equityChange : 0;

  // Långfristiga skulder (23xx) - skulder är negativa i SIE
  const currLongTermLoans = Math.abs(currBS.totalLongTermLiabilities || 0);
  const prevLongTermLoans = Math.abs(prevBS.totalLongTermLiabilities || 0);
  const loansChange = currLongTermLoans - prevLongTermLoans;
  
  const loansReceived = loansChange > 0 ? loansChange : 0;
  const loansRepaid = loansChange < 0 ? Math.abs(loansChange) : 0;

  const financingTotal = newEquity - dividendPaid + loansReceived - loansRepaid;

  // ========================================
  // SAMMANSTÄLLNING
  // ========================================

  const yearCashFlow = operatingTotal + investingTotal + financingTotal;

  // Likvida medel (19xx)
  const openingCash = sumAccountsInRange(prevBS.currentAssets, '1900', '1999');
  const closingCash = sumAccountsInRange(currBS.currentAssets, '1900', '1999');
  
  // Kontroll: beräknat kassaflöde vs faktisk förändring
  const actualCashChange = closingCash - openingCash;
  const cashFlowCheck = yearCashFlow - actualCashChange;
  const isBalanced = Math.abs(cashFlowCheck) < 1; // Tillåt 1 kr avrundning

  return {
    operatingActivities: {
      operatingResult,
      depreciation,
      provisionsChange,
      otherNonCashItems,
      subtotalBeforeWorkingCapital,
      interestReceived,
      interestPaid,
      taxPaid,
      cashFlowBeforeWorkingCapital,
      inventoryChange,
      receivablesChange,
      otherCurrentAssetsChange,
      payablesChange,
      otherCurrentLiabilitiesChange,
      workingCapitalChange,
      total: operatingTotal,
    },
    investingActivities: {
      tangibleAssetsPurchase,
      tangibleAssetsSale,
      intangibleAssetsChange,
      financialAssetsChange,
      total: investingTotal,
    },
    financingActivities: {
      newEquity,
      dividendPaid,
      loansReceived,
      loansRepaid,
      total: financingTotal,
    },
    yearCashFlow,
    openingCash,
    closingCash,
    cashFlowCheck,
    isBalanced,
    debug: {
      rawCurrentEquity: rawCurrEquity,
      rawPreviousEquity: rawPrevEquity,
      netResultUsed: netResult,
      currEquityExResult,
      equityChange,
      operatingResultRaw: incomeStatement.operatingResult,
      financialIncomeRaw: incomeStatement.totalFinancialIncome || 0,
      financialExpensesRaw: incomeStatement.totalFinancialExpenses || 0,
      resultAfterFinancialRaw: incomeStatement.resultAfterFinancial,
    },
  };
}
